import React, { FC, useState, useEffect } from "react";
import { CloseThin } from "~/components/Icons/fill";
import CancelModalStyled from "./CancelModalStyled";
import Button from "~/components/Button";
import { useModal } from "~/hooks/useModal";
import { DOMAINS_EXTERNAL } from "~/constants/config/common";
import { trackEvent } from "~/helpers/tracking";
interface CancelOrderModalProps {
  visible: boolean;
  onContinue?: () => void;
  onCancel?: () => void;
  countdown?: number;
}

// Type definitions for shell interface communication
interface ShellInterface {
  notifyPlanbuilder?: (message: string) => void;
  initShellApp?: boolean;
}

interface WindowWithWebView extends Omit<Window, "external"> {
  chrome?: {
    webview?: {
      hostObjects?: {
        external?: ShellInterface;
      };
    };
  };
  external?: ShellInterface;
}

const CancelOrderModal: FC<CancelOrderModalProps> = ({ visible }) => {
  const { closeModal } = useModal();
  const [hasTrackedView, setHasTrackedView] = useState(false);

  useEffect(() => {
    if (visible && !hasTrackedView) {
      trackEvent({
        action: "viewTimeoutModalPopup",
        data: {
          elementType: "Modal",
          text: "Timeout Modal For Kiosk Flow",
        },
      });
      setHasTrackedView(true);
    }
  }, [visible]);

  if (!visible) return null;

  const handleContinue = () => {
    closeModal();

    trackEvent({
      action: "clickContinueTimeoutButton",
      data: {
        elementType: "Button",
        text: "Continue button click on Kiosk Flow",
      },
    });
  };

  /**
   * Gets the shell interface for kiosk communication
   * Prioritizes modern WebView2 API over legacy window.external
   */
  const getShellInterface = (): ShellInterface | null => {
    const windowWithWebView = window as WindowWithWebView;

    // Prioritize WebView2 (modern approach)
    if (windowWithWebView.chrome?.webview?.hostObjects?.external) {
      return windowWithWebView.chrome.webview.hostObjects.external;
    }

    // Fallback to legacy window.external (for backwards compatibility)
    if (windowWithWebView.external?.initShellApp) {
      return windowWithWebView.external;
    }

    return null;
  };

  const handleCancel = () => {
    closeModal();

    trackEvent({
      action: "clickCancelTimeoutButton",
      data: {
        elementType: "Button",
        text: "Cancel button click on Kiosk Flow",
      },
    });

    const shellInterface = getShellInterface();
    if (shellInterface?.notifyPlanbuilder) {
      try {
        shellInterface.notifyPlanbuilder(JSON.stringify({ action: "close" }));
      } catch (error) {
        console.error("Failed to notify planbuilder:", error);
      }
    } else {
      console.warn("Shell interface not available, redirecting to Verizon Fios URL");
      window.location.href = process.env.verizonFiosUrl || DOMAINS_EXTERNAL.vzFios;
    }
  };

  const handleClose = () => {
    closeModal();
  };

  return (
    <CancelModalStyled>
      <div className="modal-overlay" />
      <div className="modal-card">
        <CloseThin onClick={handleClose} className="close-button" />
        <h3 className="heading">Are you sure you want to cancel your order?</h3>
        <div className="button-group">
          <Button onClick={handleContinue} className="continue-btn">
            CONTINUE ORDER
          </Button>

          <Button onClick={handleCancel} className="cancel-btn">
            CANCEL ORDER
          </Button>
        </div>
      </div>
    </CancelModalStyled>
  );
};

CancelOrderModal.displayName = "CancelOrderModal";

export default CancelOrderModal;
